import { RenderProofStatus } from './api/RenderProofResponse';
import { RenderProofExperience } from './RenderProofExperience';

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/** Both the surface a proof was viewed on and the surface a CTA was taken from. */
export type RenderProofSurface = 'integration_wizard' | 'dashboard';

/**
 * Why the proof could not be shown.
 *
 * - `timeout` — the render was still on its way when we stopped waiting
 * - `error` — the proof request failed
 * - `no_comparable_data` — a proof came back with nothing usable to show
 */
export type RenderProofUnavailableReason =
  | 'timeout'
  | 'error'
  | 'no_comparable_data';

export type RenderProofAnalyticsEventProperties = {
  'Integration: Render Proof Viewed': {
    location: RenderProofSurface;
    domain: string;
    // domain_id / environment_id are not resolvable from the frontend today
    // (no stable domain id or environment id is exposed to the dashboard).
    // Typed optional and omitted rather than fabricated — a documented gap.
    domain_id?: string;
    environment_id?: string;
    render_proof_status: RenderProofStatus;
    /**
     * The HTTP status of the render being shown. `render_proof_status` says
     * whether the *proof* resolved; without this, a warning screen over a 404
     * and a success screen are indistinguishable in the data — so the
     * experiment cannot tell whether the warning state converts.
     */
    render_status_code?: number;
    days_since_integration: number;
    render_proof_experience: RenderProofExperience;
  };
  /**
   * The customer was eligible for the proof and did not get one. Product asked
   * for the failure to be invisible to the customer but tracked internally:
   * without this the experiment cannot separate "the proof did not help" from
   * "the proof never appeared".
   */
  'Integration: Render Proof Unavailable': {
    location: RenderProofSurface;
    domain: string;
    reason: RenderProofUnavailableReason;
    days_since_integration: number;
    render_proof_experience: RenderProofExperience;
  };
  /** The dashboard banner was declined — the clearest negative signal it emits. */
  'Integration: Render Proof Dismissed': {
    location: RenderProofSurface;
    days_since_integration: number;
    render_proof_experience: RenderProofExperience;
  };
  'Status codes doc viewed': {
    source: 'render_proof';
    source_location: RenderProofSurface;
  };
  'Sitemap page viewed': {
    source: 'render_proof';
    source_location: RenderProofSurface;
  };
  'Dashboard viewed': {
    source: 'render_proof';
    source_location: RenderProofSurface;
  };
};

/**
 * The host the proof is actually for.
 *
 * `proof.url` is present and authoritative whenever a proof is on screen, where
 * the wizard's separately-cached domain can be empty. Only the host goes into
 * the event, since the property is `domain` — sending a full URL under that name
 * would change what the dimension means.
 */
export function proofDomain(proofUrl: string, fallback: string): string {
  try {
    return new URL(proofUrl).host;
  } catch {
    return fallback;
  }
}

export function daysSinceIntegration(
  trackingCodeInstalledDate: Date | string | null,
  now: Date,
): number {
  if (trackingCodeInstalledDate === null) return 0;

  const integratedAt = new Date(trackingCodeInstalledDate);
  // Clock skew, or an integration date in the future, would otherwise send a
  // negative day count into the experiment's data.
  return Math.max(
    0,
    Math.floor((now.getTime() - integratedAt.getTime()) / MILLISECONDS_PER_DAY),
  );
}
